'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { MinimalWave } from '@/contexts/wave/hooks/useEnhancedWavesList';
import { usePrefetchWaveData } from '@/hooks/usePrefetchWaveData';
import useDeviceInfo from '@/hooks/useDeviceInfo';
import { ApiWaveType } from '@/generated/models/ApiWaveType';
import { CollapsedWave } from './subcomponents/CollapsedWave';
import { ExpandedWave } from './subcomponents/ExpandedWave';
import { useWaveNavigation } from './hooks/useWaveNavigation';
import { useWaveNameTruncation } from './hooks/useWaveNameTruncation';
import { formatWaveName } from './utils/formatWaveName';
import { useMyStream } from '@/contexts/wave/MyStreamContext';
interface WebBrainLeftSidebarWaveProps {
  readonly wave: MinimalWave;
  readonly onHover: (waveId: string) => void;
  readonly showPin?: boolean;
  readonly basePath?: string;
  readonly collapsed?: boolean;
}
const TOOLTIP_PLACEMENT = 'right' as const;
const WebBrainLeftSidebarWave = t0 => {
  const $ = _c(39);
  const {
    wave,
    onHover,
    showPin: t1,
    basePath: t2,
    collapsed: t3
  } = t0;
  const showPin = t1 === undefined ? true : t1;
  const basePath = t2 === undefined ? "/waves" : t2;
  const collapsed = t3 === undefined ? false : t3;
  const {
    activeWave
  } = useMyStream();
  const searchParams = useSearchParams();
  const prefetchWaveData = usePrefetchWaveData();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  let t4;
  if ($[0] !== activeWave.id || $[1] !== activeWave.set || $[2] !== basePath || $[3] !== hasTouchScreen || $[4] !== onHover || $[5] !== prefetchWaveData || $[6] !== searchParams || $[7] !== wave.id) {
    t4 = {
      basePath,
      activeWaveId: activeWave.id,
      setActiveWave: activeWave.set,
      onHover,
      prefetchWaveData,
      searchParams,
      waveId: wave.id,
      hasTouchScreen
    };
    $[0] = activeWave.id;
    $[1] = activeWave.set;
    $[2] = basePath;
    $[3] = hasTouchScreen;
    $[4] = onHover;
    $[5] = prefetchWaveData;
    $[6] = searchParams;
    $[7] = wave.id;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    href,
    isActive,
    onMouseEnter,
    onClick
  } = useWaveNavigation(t4);
  let t5;
  if ($[9] !== wave.name || $[10] !== wave.type) {
    t5 = formatWaveName({
      name: wave.name,
      type: wave.type
    });
    $[9] = wave.name;
    $[10] = wave.type;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const formattedWaveName = t5;
  let t6;
  if ($[12] !== collapsed || $[13] !== formattedWaveName) {
    t6 = {
      collapsed,
      contentKey: formattedWaveName
    };
    $[12] = collapsed;
    $[13] = formattedWaveName;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    nameRef,
    isNameTruncated
  } = useWaveNameTruncation(t6);
  const isDropWave = wave.type !== ApiWaveType.Chat;
  const haveNewDrops = wave.newDropsCount.count > 0;
  const tooltipId = `wave-collapsed-${wave.id}`;
  const expandedTooltipId = `wave-expanded-${wave.id}`;
  const showCollapsedTooltip = collapsed && !hasTouchScreen;
  const showExpandedTooltip = !collapsed && !hasTouchScreen && isNameTruncated;
  if (collapsed) {
    let t7;
    if ($[15] !== formattedWaveName || $[16] !== haveNewDrops || $[17] !== href || $[18] !== isActive || $[19] !== isDropWave || $[20] !== onClick || $[21] !== onMouseEnter || $[22] !== showCollapsedTooltip || $[23] !== tooltipId || $[24] !== wave) {
      t7 = <CollapsedWave formattedWaveName={formattedWaveName} haveNewDrops={haveNewDrops} href={href} isActive={isActive} isDropWave={isDropWave} onMouseEnter={onMouseEnter} onClick={onClick} showTooltip={showCollapsedTooltip} tooltipId={tooltipId} tooltipPlacement={TOOLTIP_PLACEMENT} wave={wave} />;
      $[15] = formattedWaveName;
      $[16] = haveNewDrops;
      $[17] = href;
      $[18] = isActive;
      $[19] = isDropWave;
      $[20] = onClick;
      $[21] = onMouseEnter;
      $[22] = showCollapsedTooltip;
      $[23] = tooltipId;
      $[24] = wave;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    return t7;
  }
  let t7;
  if ($[26] !== expandedTooltipId || $[27] !== formattedWaveName || $[28] !== haveNewDrops || $[29] !== href || $[30] !== isActive || $[31] !== isDropWave || $[32] !== nameRef || $[33] !== onClick || $[34] !== onMouseEnter || $[35] !== showExpandedTooltip || $[36] !== showPin || $[37] !== wave) {
    t7 = <ExpandedWave formattedWaveName={formattedWaveName} haveNewDrops={haveNewDrops} href={href} isActive={isActive} isDropWave={isDropWave} latestDropTimestamp={wave.newDropsCount.latestDropTimestamp} nameRef={nameRef} onMouseEnter={onMouseEnter} onClick={onClick} showExpandedTooltip={showExpandedTooltip} showPin={showPin} tooltipContent={formattedWaveName} tooltipId={expandedTooltipId} tooltipPlacement={TOOLTIP_PLACEMENT} wave={wave} waveId={wave.id} isPinned={wave.isPinned} />;
    $[26] = expandedTooltipId;
    $[27] = formattedWaveName;
    $[28] = haveNewDrops;
    $[29] = href;
    $[30] = isActive;
    $[31] = isDropWave;
    $[32] = nameRef;
    $[33] = onClick;
    $[34] = onMouseEnter;
    $[35] = showExpandedTooltip;
    $[36] = showPin;
    $[37] = wave;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  return t7;
};
export default WebBrainLeftSidebarWave;